import { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, TrendingUp, TrendingDown, Minus, Info, AlertTriangle } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BenchmarkComparisonCard — Phase 4 (Jun 2026), Round 96g (Feb 2026)
 * non-dividend variant, Round P108au (Feb 2026) family-aware routing.
 *
 * Single-stock total-return comparison vs a 3-benchmark trio over
 * 1Y, 3Y, and 5Y windows. Uses DRIP-on math (every cash dividend
 * rebought at that day's close).
 *
 * Endpoint: GET /api/watchlist/time-machine/{ticker}?years={1|3|5}&benchmarks={dividend|growth}
 *   → { total_return_pct, benchmarks: { ... }, benchmark_family,
 *       benchmark_family_label, benchmark_tickers, hide_benchmark_card, ... }
 *
 * Three trio modes:
 *   • "dividend" (default) → SPY / SCHD / VYM.
 *   • "growth" → SPYG / QQQ / IWF for non-dividend stocks.
 *   • Backend-classified family override — for international / EM /
 *     bond / REIT / FX ETFs the backend swaps the trio automatically
 *     (e.g. EM ex-China → EMXC / XCEM / IEMG). Card shows a
 *     "Compared against: [Category]" caption + a "Why these?" tooltip
 *     explaining the routing.
 *
 * Crypto or delisted funds hide the trio entirely and show a
 * compact fallback banner explaining why the comparison isn't
 * meaningful for that asset.
 */

const PERIODS = [
  { years: 1, label: '1Y' },
  { years: 3, label: '3Y' },
  { years: 5, label: '5Y' },
];

// ETF blurbs used by the delta-cell hover tooltip. Covers every
// ticker that can appear in a family trio (backend classifier +
// legacy dividend/growth defaults). Terse one-liners describing
// what each ETF actually tracks so a user hovering "vs EMXC" sees
// something useful, not just "vs EMXC".
const ETF_BLURBS = {
  // Legacy dividend / growth trios
  SPY: 'SPY — SPDR S&P 500 ETF. The broad U.S. market benchmark.',
  SCHD: 'SCHD — Schwab U.S. Dividend Equity ETF. Quality-dividend strategy benchmark.',
  VYM: 'VYM — Vanguard High Dividend Yield ETF. Broad high-yield benchmark.',
  SPYG: 'SPYG — SPDR S&P 500 Growth ETF. Isolates large-cap growth from the S&P 500.',
  QQQ: 'QQQ — Invesco NASDAQ-100 ETF. Tech-heavy mega-caps that reinvest over distributing.',
  IWF: 'IWF — iShares Russell 1000 Growth. Pure large-cap growth factor.',
  // Developed markets
  EFA: 'EFA — iShares MSCI EAFE. Vanilla unhedged developed markets (Europe, Australasia, Far East).',
  IEFA: 'IEFA — iShares Core MSCI EAFE. Broad unhedged developed markets, cheaper twin of EFA.',
  SPDW: 'SPDW — SPDR Portfolio Developed World ex-US. Core unhedged developed-markets benchmark.',
  DBEF: 'DBEF — Xtrackers MSCI EAFE Hedged Equity. USD-hedged developed markets baseline.',
  HEFA: 'HEFA — iShares Currency Hedged MSCI EAFE. USD-hedged EAFE alternative to DBEF.',
  VSS: 'VSS — Vanguard FTSE All-World ex-US Small-Cap. Broad developed + EM small-cap benchmark.',
  // Emerging markets
  IEMG: 'IEMG — iShares Core MSCI Emerging Markets. Vanilla unhedged EM benchmark.',
  VWO: 'VWO — Vanguard FTSE Emerging Markets. Alt broad unhedged EM.',
  EMXC: 'EMXC — iShares MSCI Emerging Markets ex-China. Institutional baseline for ex-China EM.',
  XCEM: 'XCEM — Columbia EM Core ex-China. Alt ex-China EM benchmark.',
  DBEM: 'DBEM — Xtrackers MSCI EM Hedged Equity. USD-hedged EM baseline.',
  HEEM: 'HEEM — iShares Currency Hedged MSCI EM. USD-hedged EM alt to DBEM.',
  // Regional
  EPP: 'EPP — iShares MSCI Pacific ex-Japan. Regional Asia-Pacific benchmark.',
  VNQI: 'VNQI — Vanguard Global ex-U.S. Real Estate. Definitive international REIT benchmark.',
  // Bonds
  BNDX: 'BNDX — Vanguard Total International Bond ETF. Broad USD-hedged international bond gold standard.',
  IAGG: 'IAGG — iShares Core International Aggregate Bond. Alt USD-hedged intl bond benchmark.',
  BWX: 'BWX — SPDR Bloomberg International Treasury Bond. Broad unhedged intl bond benchmark.',
  ISHG: 'ISHG — iShares 1-3 Year International Treasury Bond. Short-duration unhedged intl bond benchmark.',
  EMB: 'EMB — iShares JPMorgan USD Emerging Markets Bond. USD-denominated EM sovereign debt.',
  EMLC: 'EMLC — VanEck JPMorgan EM Local Currency Bond. Local-currency EM debt benchmark.',
  // FX
  UUP: 'UUP — Invesco DB US Dollar Index Bullish Fund. The industry-standard USD-strength benchmark (long USD, effectively short a G10 basket). Replaces the legacy DBV, which was liquidated in March 2023.',
  DBV: 'DBV — Invesco DB G10 Currency Harvest (LIQUIDATED March 2023). Historical reference only — use UUP as the live replacement.',
  ELD: 'ELD — WisdomTree Emerging Markets Local Debt. EM currency + rates blended benchmark.',
  CEW: 'CEW — WisdomTree Emerging Currency Strategy. Basket of EM currency futures.',
};

// One-line description of *why* a family gets its specific trio.
// Used by the "Why these?" info tooltip. Keeps the explanation
// consistent with the user-verified classification flowchart.
const FAMILY_REASONS = {
  developed_hedged_equity:   'This fund invests in developed international markets and hedges currency risk back to USD. Comparing it to SPY would blend an asset-class mismatch (US vs EAFE) with a strategy mismatch (hedged vs unhedged). DBEF / HEFA / EFA isolate whether the hedge and stock-picking actually add value.',
  developed_unhedged_equity: 'This fund tracks developed international markets without hedging currency risk. Its returns include foreign FX movements against the USD, so it must be benchmarked against unhedged EAFE peers — EFA / IEFA / SPDW.',
  developed_small_cap:       'This is a developed-markets small-cap fund. Small-caps have their own risk / return profile distinct from large-cap EAFE, so we benchmark against VSS (broad ex-US small-cap) plus EFA / IEFA for large-cap context.',
  em_ex_china:               'This fund explicitly excludes China from its emerging-markets exposure. Standard EM benchmarks (IEMG, VWO) are ~25% China, so we route to EMXC / XCEM / IEMG — the first two are ex-China; IEMG is included as a "if you did hold China" reference.',
  em_hedged_equity:          'This EM fund hedges currency risk back to USD. Comparing it to unhedged EM benchmarks would confuse the hedge cost with stock-picking, so we use DBEM / HEEM (both USD-hedged EM) plus IEMG for the unhedged reference.',
  em_unhedged_equity:        'This is a standard unhedged emerging-markets equity fund. We benchmark against IEMG / VWO (the two most-followed EM indices) plus EMXC to show what the return would have been without China.',
  pacific_ex_japan:          'This fund targets Asia-Pacific developed markets but excludes Japan (the biggest single-country weight in most EAFE benchmarks). EPP is the definitive Pacific ex-Japan benchmark; SPDW / EFA provide broad-EAFE context.',
  intl_reit:                 'International real estate has a very different volatility and cash-flow profile from broad international equity. VNQI is the definitive global ex-US REIT benchmark; SPDW / EFA show what a broad developed-markets index did in the same window.',
  intl_bond_hedged:          'This is a broad international bond fund that hedges currency risk. Currency-hedged intl bond funds should be benchmarked against other hedged intl bond funds — BNDX / IAGG — not equity ETFs. BWX shows the unhedged variant for reference.',
  intl_bond_unhedged_broad:  'This is a broad international bond fund without currency hedging. Its returns swing on FX movements, so we benchmark against BWX (the unhedged gold standard) plus BNDX / IAGG for the hedged comparison.',
  intl_bond_short:           'This is a short-duration international bond fund (1-3 year maturities). Ultra-low interest-rate sensitivity means broad intl bond benchmarks would distort the comparison, so we route to ISHG plus BWX / BNDX for context.',
  em_bond_usd:               'This is a USD-denominated emerging-markets bond fund. Currency risk is minimal so the comparison is really about EM credit quality. EMB is the industry-standard USD EM bond benchmark; EMLC / BNDX provide local-currency and hedged-intl reference.',
  em_bond_local:             'This is a local-currency emerging-markets bond fund. Returns include EM currency movements against the USD. EMLC is the standard local-currency EM bond benchmark; EMB / BNDX show the USD-denominated and hedged-intl variants.',
  fx_strategy:               'This is a pure currency / FX strategy fund — not an equity or bond fund. Equity or bond benchmarks would be meaningless. DBV / ELD / CEW are the go-to broad FX strategy benchmarks.',
};

// Family → AbbrTip term key. Lets the "Compared against: [Label]"
// caption wrap the label in an AbbrTip that links to the correct
// Glossary entry per family.
const FAMILY_ABBR_TERM = {
  developed_hedged_equity:   'BENCHMARK_FAMILY_HEDGED_EQUITY',
  developed_unhedged_equity: 'BENCHMARK_FAMILY_UNHEDGED_EQUITY',
  developed_small_cap:       'BENCHMARK_FAMILY_SMALL_CAP',
  em_ex_china:               'BENCHMARK_FAMILY_EM_EX_CHINA',
  em_hedged_equity:          'BENCHMARK_FAMILY_EM_HEDGED',
  em_unhedged_equity:        'BENCHMARK_FAMILY_EM_UNHEDGED',
  pacific_ex_japan:          'BENCHMARK_FAMILY_PACIFIC_EX_JAPAN',
  intl_reit:                 'BENCHMARK_FAMILY_INTL_REIT',
  intl_bond_hedged:          'BENCHMARK_FAMILY_INTL_BOND_HEDGED',
  intl_bond_unhedged_broad:  'BENCHMARK_FAMILY_INTL_BOND_UNHEDGED',
  intl_bond_short:           'BENCHMARK_FAMILY_INTL_BOND_SHORT',
  em_bond_usd:               'BENCHMARK_FAMILY_EM_BOND_USD',
  em_bond_local:             'BENCHMARK_FAMILY_EM_BOND_LOCAL',
  fx_strategy:               'BENCHMARK_FAMILY_FX_STRATEGY',
};

function DeltaCell({ name, stockReturn, benchmarkReturn, blurbs, tipMethod, isSelf }) {
  if (isSelf) {
    // Round P108au+1 — when the ticker IS one of the benchmarks in
    // its own family trio (e.g. EMXC in the EM-ex-China trio), the
    // delta is trivially 0.0% and adds no signal. Show a compact
    // "self" indicator instead so the user isn't misled into
    // reading it as "matched the benchmark".
    return (
      <div className="flex flex-col items-end" data-testid={`bench-delta-${name}`}>
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">vs {name}</span>
        <span
          className="text-xs font-mono text-muted-foreground italic cursor-help"
          title={`${name} is this ticker — comparing it against itself is always 0.0%, so we've hidden the delta.`}
        >
          — self
        </span>
      </div>
    );
  }
  if (benchmarkReturn == null || stockReturn == null) {
    return (
      <div className="flex flex-col items-end" data-testid={`bench-delta-${name}`}>
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">vs {name}</span>
        <span className="text-xs font-mono text-muted-foreground">—</span>
      </div>
    );
  }
  const delta = stockReturn - benchmarkReturn;
  const pos = delta >= 0;
  const method = tipMethod || '(price + dividends, DRIP on)';
  const blurb = blurbs[name] || ETF_BLURBS[name] || '';
  const tip = `${name} returned ${benchmarkReturn >= 0 ? '+' : ''}${benchmarkReturn.toFixed(2)}% over the same window ${method}. The stock returned ${stockReturn >= 0 ? '+' : ''}${stockReturn.toFixed(2)}%, ${pos ? `beating ${name} by ${Math.abs(delta).toFixed(2)}%.` : `lagging ${name} by ${Math.abs(delta).toFixed(2)}%.`} ${blurb}`;
  return (
    <div
      className="flex flex-col items-end cursor-help"
      title={tip}
      data-testid={`bench-delta-${name}`}
    >
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">vs {name}</span>
      <span className={`text-xs font-mono font-bold ${pos ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
        {pos ? '+' : ''}{delta.toFixed(1)}%
      </span>
    </div>
  );
}

function VerdictPill({ stockReturn, benchmarks, tickers, ticker }) {
  if (stockReturn == null) return null;
  // Exclude self-comparisons — they're trivially zero and would
  // wrongly force a ticker in its own family trio into a "Mixed"
  // verdict when it actually beat all *real* peers.
  const upper = (ticker || '').toUpperCase();
  const peerTickers = tickers.filter((t) => t.toUpperCase() !== upper);
  const valid = peerTickers
    .map((b) => benchmarks?.[b])
    .filter((v) => v != null);
  if (valid.length === 0) return null;

  const beatCount = valid.filter((b) => stockReturn > b).length;
  const labelList = peerTickers.join(', ');

  if (beatCount === valid.length) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 ring-1 ring-emerald-400/40"
        title={`Beat all ${valid.length} benchmark${valid.length === 1 ? '' : 's'} (${labelList}) for this period.`}
      >
        <TrendingUp className="w-3 h-3" /> Beat all
      </span>
    );
  }
  if (beatCount === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 ring-1 ring-rose-400/40"
        title={`Lagged every benchmark (${labelList}) for this period.`}
      >
        <TrendingDown className="w-3 h-3" /> Lagged all
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 ring-1 ring-amber-400/40"
      title={`Mixed result — beat ${beatCount} of ${valid.length} benchmarks for this period.`}
    >
      <Minus className="w-3 h-3" /> Mixed
    </span>
  );
}

function PeriodRow({ ticker, years, label, mode, tickers, tipMethod, blurbs, onMeta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    axios
      .get(`${API}/watchlist/time-machine/${encodeURIComponent(ticker)}`, {
        params: { years, benchmarks: mode },
        withCredentials: true,
      })
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        if (onMeta && r.data) {
          onMeta({
            family: r.data.benchmark_family,
            familyLabel: r.data.benchmark_family_label,
            familyTickers: r.data.benchmark_tickers,
            hideCard: !!r.data.hide_benchmark_card,
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [ticker, years, mode, onMeta]);

  if (loading) {
    return (
      <div className="flex items-center justify-between py-2 border-b border-border/30 last:border-0" data-testid={`bench-row-${years}y-loading`}>
        <span className="text-xs font-bold text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground italic">Loading…</span>
      </div>
    );
  }

  if (!data || data.total_return_pct == null) {
    return (
      <div className="flex items-center justify-between py-2 border-b border-border/30 last:border-0" data-testid={`bench-row-${years}y-empty`}>
        <span className="text-xs font-bold text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground italic" title={`Not enough price history for ${ticker} over ${years}y window — usually means the ticker is younger than ${years} year${years === 1 ? '' : 's'} or the data source has a gap.`}>Insufficient history</span>
      </div>
    );
  }

  const stockReturn = data.total_return_pct;
  const isProfit = stockReturn >= 0;
  const periodMonths = data.fallback_period_months;
  const labelSuffix = periodMonths && periodMonths !== years * 12
    ? ` (${periodMonths}mo only — ${ticker} listed mid-window)`
    : '';

  return (
    <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-center py-2 border-b border-border/30 last:border-0" data-testid={`bench-row-${years}y`}>
      {/* Period label + stock return */}
      <div className="col-span-2 flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-foreground w-8">{label}</span>
        <div className="flex flex-col">
          <span className={`text-sm font-mono font-bold ${isProfit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {isProfit ? '+' : ''}{stockReturn.toFixed(2)}%
          </span>
          {labelSuffix && (
            <span className="text-[9px] text-amber-600 dark:text-amber-400 font-bold">{labelSuffix}</span>
          )}
        </div>
        <VerdictPill stockReturn={stockReturn} benchmarks={data.benchmarks || {}} tickers={tickers} ticker={ticker} />
      </div>

      {/* Three benchmark deltas — dynamically driven by the family trio
          returned from the backend (falls back to the mode default).
          Self-comparison (ticker == benchmark) is flagged so we can
          hide the trivially-zero delta cell. */}
      {tickers.map((bm) => (
        <DeltaCell
          key={bm}
          name={bm}
          stockReturn={stockReturn}
          benchmarkReturn={data.benchmarks?.[bm]}
          blurbs={blurbs}
          tipMethod={tipMethod}
          isSelf={bm.toUpperCase() === (ticker || '').toUpperCase()}
        />
      ))}
    </div>
  );
}

// Round P108au fallback banner for delisted / crypto / other
// non-comparable assets. Replaces the whole trio with a compact
// single-line explanation so the card doesn't just disappear silently.
function FallbackBanner({ variant, familyLabel }) {
  const isDelisted = variant === 'delisted';
  const message = isDelisted
    ? `${familyLabel || 'This fund'} — benchmark comparisons are historical only. The ETF has been liquidated and no longer trades.`
    : `${familyLabel || 'Crypto asset'} — not directly comparable to equity or bond benchmarks. Total return is driven by protocol / token supply mechanics rather than any standard index.`;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-400/40 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 px-3 py-2 text-xs" data-testid={`benchmark-fallback-${variant}`}>
      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-[1px]" />
      <div>
        <div className="font-bold uppercase tracking-wider text-[10px] mb-0.5">
          {isDelisted ? 'Fund liquidated' : 'Not comparable'}
        </div>
        {message}
      </div>
    </div>
  );
}

// Round 96g (Jul 2026) — "vs My Portfolio (3M)" comparison row was
// briefly rendered here inside the stock modal, then relocated to
// the PortfolioBlendCompareCard on the Watchlist per user request.
function PortfolioDeltaRow() { return null; }

// Legacy (dividend / growth) mode config. The backend family-based
// override supersedes this whenever it classifies the ticker into
// an international / EM / bond / etc. family.
const LEGACY_MODES = {
  dividend: {
    tickers: ['SPY', 'SCHD', 'VYM'],
    headerTip: "Total return with DRIP on (every cash dividend rebought at that day's close). Stock's return on the left, deltas vs SPY / SCHD / VYM on the right.",
    deltaTipMethod: '(price + dividends, DRIP on)',
    footer: 'SPY = S&P 500. SCHD = quality dividends. VYM = broad high-yield. Tap or hover any cell for the underlying numbers.',
    blurbs: {
      SPY: ETF_BLURBS.SPY,
      SCHD: ETF_BLURBS.SCHD,
      VYM: ETF_BLURBS.VYM,
    },
  },
  growth: {
    tickers: ['SPYG', 'QQQ', 'IWF'],
    headerTip: "Total return over each window (price change plus any incidental ETF distributions reinvested). Stock's return on the left, deltas vs growth-focused benchmarks on the right — the right yardstick for non-dividend stocks that prioritize reinvestment over distribution.",
    deltaTipMethod: '(total return — capital appreciation plus any distributions reinvested)',
    footer: 'SPYG = S&P 500 Growth. QQQ = NASDAQ-100 (tech-heavy growth). IWF = Russell 1000 Growth (pure growth factor). Tap or hover any cell for the underlying numbers.',
    blurbs: {
      SPYG: ETF_BLURBS.SPYG,
      QQQ: ETF_BLURBS.QQQ,
      IWF: ETF_BLURBS.IWF,
    },
  },
};

export default function BenchmarkComparisonCard({ ticker, mode = 'dividend', peg = null }) {
  const [meta, setMeta] = useState(null);
  if (!ticker) return null;

  // Delisted / crypto fallback banner — replaces the whole trio.
  if (meta?.hideCard) {
    return (
      <div className="rounded-lg border bg-card p-3" data-testid={`benchmark-comparison-card-${meta.family || 'hidden'}`}>
        <div className="flex items-center gap-1.5 mb-2">
          <LineChart className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" />
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">
            Benchmark Performance
          </h4>
        </div>
        <FallbackBanner variant={meta.family} familyLabel={meta.familyLabel} />
      </div>
    );
  }

  // Family override (backend classifier) OR legacy dividend/growth default.
  const legacy = LEGACY_MODES[mode] || LEGACY_MODES.dividend;
  const familyTickers = meta?.familyTickers && meta.familyTickers.length ? meta.familyTickers : null;
  const tickers = familyTickers || legacy.tickers;
  const headerTitle = `Total Return · vs ${tickers.join(' / ')}`;
  const isFamilyOverride = !!(meta?.family && familyTickers);
  const familyReason = isFamilyOverride ? FAMILY_REASONS[meta.family] : null;
  const headerTip = isFamilyOverride
    ? `Total return over each window (price change plus any distributions reinvested). Stock's return on the left, deltas vs ${meta.familyLabel} peers on the right.`
    : legacy.headerTip;
  const deltaTipMethod = isFamilyOverride
    ? '(total return — capital appreciation plus any distributions reinvested)'
    : legacy.deltaTipMethod;
  const blurbs = isFamilyOverride
    ? Object.fromEntries(tickers.map((t) => [t, ETF_BLURBS[t] || '']))
    : legacy.blurbs;
  const footer = isFamilyOverride
    ? `${tickers.join(' · ')} — routed automatically because ${ticker} was classified as ${meta.familyLabel}. Tap or hover any cell for the underlying numbers.`
    : legacy.footer;

  return (
    <div className="rounded-lg border bg-card p-3" data-testid={`benchmark-comparison-card-${meta?.family || mode}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="flex items-center gap-1.5">
          <LineChart className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" />
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">
            Benchmark Performance
          </h4>
        </div>
        <span
          className="text-[10px] font-bold text-muted-foreground cursor-help"
          title={headerTip}
        >
          {headerTitle}
        </span>
      </div>

      {/* Round P108au — family override caption + "Why these?" tooltip.
          Only rendered when the backend classifier fires (int'l / EM /
          bond / REIT / FX). Falls back to the legacy dividend / growth
          layout when no family is detected. Both the family label AND
          the "Why these?" text are wrapped in AbbrTips that link to
          the canonical Glossary entry in a new tab. */}
      {isFamilyOverride && (
        <div
          className="mb-3 flex items-center gap-2 rounded-md border border-fuchsia-400/30 bg-fuchsia-50 dark:bg-fuchsia-950/20 text-fuchsia-700 dark:text-fuchsia-200 px-2.5 py-1.5"
          data-testid="benchmark-family-caption"
        >
          <span className="text-[10px] font-bold uppercase tracking-wider">
            Compared against:
          </span>
          <span className="text-xs font-bold" data-testid="benchmark-family-label">
            <AbbrTip term={FAMILY_ABBR_TERM[meta.family] || 'BENCHMARK_FAMILY_WHY_THESE'}>
              {meta.familyLabel}
            </AbbrTip>
          </span>
          <span
            className="inline-flex items-center gap-0.5 ml-auto text-[10px] font-bold cursor-help hover:text-fuchsia-900 dark:hover:text-fuchsia-100 transition-colors"
            data-testid="benchmark-family-why-tooltip"
          >
            <AbbrTip term="BENCHMARK_FAMILY_WHY_THESE">
              <span className="inline-flex items-center gap-0.5">
                <Info className="w-3 h-3" /> Why these?
              </span>
            </AbbrTip>
          </span>
        </div>
      )}

      {/* PEG ratio pill on the growth variant only (never on family
          overrides — ETFs don't have a meaningful PEG). */}
      {!isFamilyOverride && mode === 'growth' && peg != null && Number.isFinite(peg) && (
        <PegPanel peg={peg} />
      )}

      <div className="space-y-0">
        {PERIODS.map((p) => (
          <PeriodRow
            key={p.years}
            ticker={ticker}
            years={p.years}
            label={p.label}
            mode={mode}
            tickers={tickers}
            tipMethod={deltaTipMethod}
            blurbs={blurbs}
            // Only the 1Y row needs to publish family metadata upstream —
            // it's stable across periods so we don't refetch/replay it.
            onMeta={p.years === 1 ? setMeta : undefined}
          />
        ))}
      </div>

      <p className="text-[10px] text-muted-foreground mt-2 italic">
        {footer}
      </p>
    </div>
  );
}

// PEG (Price/Earnings-to-Growth) — a 1-line pill + an expandable
// table that explains the value ranges.
function PegPanel({ peg }) {
  const [open, setOpen] = useState(false);
  const { color, label } = pegBand(peg);
  return (
    <div className="mb-3 rounded-md border border-border/40 bg-muted/20 px-3 py-2" data-testid="peg-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
            <AbbrTip term="PEG">PEG Ratio</AbbrTip>
          </span>
          <span className={`text-sm font-mono font-bold ${color}`} data-testid="peg-value">
            {peg.toFixed(2)}
          </span>
          <span className={`text-[10px] font-bold uppercase tracking-wider ${color}`}>
            · {label}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          data-testid="peg-table-toggle"
          aria-expanded={open}
        >
          <Info className="w-3 h-3" />
          {open ? 'Hide table' : 'How to read this'}
        </button>
      </div>
      {open && (
        <table className="w-full mt-2 text-[11px] border-collapse" data-testid="peg-range-table">
          <thead>
            <tr className="border-b border-border/40">
              <th className="text-left py-1 pr-3 font-bold text-muted-foreground">PEG range</th>
              <th className="text-left py-1 pr-3 font-bold text-muted-foreground">Read as</th>
              <th className="text-left py-1 font-bold text-muted-foreground">When it fits</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/30">
              <td className="py-1 pr-3 font-mono font-bold text-emerald-600 dark:text-emerald-400">&lt; 1.0</td>
              <td className="py-1 pr-3">Potentially undervalued for the growth</td>
              <td className="py-1 text-muted-foreground">Growth rate is faster than the P/E premium would suggest.</td>
            </tr>
            <tr className="border-b border-border/30">
              <td className="py-1 pr-3 font-mono font-bold text-sky-600 dark:text-sky-400">1.0 – 1.5</td>
              <td className="py-1 pr-3">Fairly valued</td>
              <td className="py-1 text-muted-foreground">Most quality growth stocks land here. Pay attention to the trend.</td>
            </tr>
            <tr className="border-b border-border/30">
              <td className="py-1 pr-3 font-mono font-bold text-amber-600 dark:text-amber-400">1.5 – 2.0</td>
              <td className="py-1 pr-3">Pricey — growth must keep delivering</td>
              <td className="py-1 text-muted-foreground">Common for mega-cap compounders (MSFT, V, MA). Watch margin trends.</td>
            </tr>
            <tr className="border-b border-border/30">
              <td className="py-1 pr-3 font-mono font-bold text-rose-600 dark:text-rose-400">&gt; 2.0</td>
              <td className="py-1 pr-3">Expensive vs growth pace</td>
              <td className="py-1 text-muted-foreground">Risk of multiple compression if growth disappoints even once.</td>
            </tr>
            <tr>
              <td className="py-1 pr-3 font-mono font-bold text-muted-foreground">&lt; 0 or N/A</td>
              <td className="py-1 pr-3">Negative or unmeasurable</td>
              <td className="py-1 text-muted-foreground">Pre-profit / early-stage / one-off accounting items. Fall back to P/S or P/CF.</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function pegBand(peg) {
  if (peg == null || !Number.isFinite(peg)) {
    return { color: 'text-muted-foreground', label: 'N/A' };
  }
  if (peg < 0) return { color: 'text-muted-foreground', label: 'negative — see table' };
  if (peg < 1.0) return { color: 'text-emerald-600 dark:text-emerald-400', label: 'undervalued for growth' };
  if (peg < 1.5) return { color: 'text-sky-600 dark:text-sky-400', label: 'fairly valued' };
  if (peg < 2.0) return { color: 'text-amber-600 dark:text-amber-400', label: 'pricey' };
  return { color: 'text-rose-600 dark:text-rose-400', label: 'expensive vs growth' };
}
